import React from 'react';
import { CalendarIcon, LayoutListIcon, MapIcon, MapPinIcon, SearchIcon, SlidersHorizontalIcon, WalletIcon, XIcon } from 'lucide-react';
import { HOUSING_CATEGORIES, useHousing, type HousingView } from '../../contexts/HousingContext';
import { campusOptions } from '../../data/institutions';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { FilterRail } from './FilterRail';
import type { PropertyKind } from '../../types/housing';

/* Controls shared by BOTH compositions. A control lives here precisely so the
   two variants cannot drift into behaving differently. */

const BUDGETS = [400000, 550000, 750000, 1000000, 2000000];
const MOVE_INS = [{
  value: '2026-09-01',
  label: 'Sept 2026'
}, {
  value: '2026-10-01',
  label: 'Oct 2026'
}, {
  value: '2027-01-01',
  label: 'Jan 2027'
}];
const KINDS: PropertyKind[] = ['Hostel', 'Student residence', 'Apartment', 'Shared apartment', 'Self-contained', 'Co-living'];
export function HousingSearchField({
  className = ''
}: {
  className?: string;
}) {
  const {
    query,
    setQuery
  } = useHousing();
  return <div className={`relative ${className}`} data-id="element-1915" data-mp="it39cj5pzuxz">
      <label htmlFor="housing-search" className="sr-only" data-id="element-1916" data-mp="is9yp73n4lak">
        Search homes by area, property name or keyword
      </label>
      <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" data-id="element-1917" data-mp="8wgq1yt1lby2" />
      <input id="housing-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by area, property name or keyword…" className="h-12 w-full rounded-full border border-line bg-surface pl-12 pr-4 text-body text-ink placeholder:text-ink-subtle" data-id="element-1918" data-mp="16ixkmwae5bg4" />
    </div>;
}
export function HousingCategoryTabs() {
  const {
    category,
    setCategory
  } = useHousing();
  return <div role="tablist" aria-label="Accommodation category" className="flex max-w-full min-w-0 gap-2 overflow-x-auto pb-1" style={{
    overscrollBehaviorX: 'contain'
  }} data-id="element-1919" data-mp="3ccdz24fgwdut">
      {HOUSING_CATEGORIES.map(entry => <button key={entry} role="tab" aria-selected={category === entry} type="button" onClick={() => setCategory(entry)} className={`min-h-[44px] shrink-0 rounded-full border px-4 text-body-sm font-medium transition-colors duration-150 ease-out ${category === entry ? 'border-brand-700 bg-brand-700 text-white hover:text-white' : 'border-line bg-surface text-ink hover:bg-canvas'}`} data-id="element-1920" data-mp="28cpi0yfsla8a">
          {entry}
        </button>)}
    </div>;
}
export function HousingFilterChips() {
  const {
    activeChips,
    clearAll
  } = useHousing();
  if (activeChips.length === 0) return null;
  return <div className="flex flex-wrap items-center gap-2" data-id="element-1921" data-mp="11spl9jso4fr4">
      {activeChips.map(chip => <span key={chip.id} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface py-1 pl-3 pr-1 text-body-sm text-ink" data-id="element-1922" data-mp="16rkc990shamb">
          {chip.label}
          <button type="button" onClick={chip.clear} aria-label={`Remove filter ${chip.label}`} className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1923" data-mp="j4fhgkzy6wm5">
            <XIcon aria-hidden className="h-4 w-4" data-id="element-1924" data-mp="160fqzpbbmrh0" />
          </button>
        </span>)}
      <button type="button" onClick={clearAll} className="min-h-[44px] rounded-full px-3 text-body-sm font-medium text-brand-700 underline-offset-2 hover:underline" data-id="element-1925" data-mp="11yuh8cu32tb1">
        Clear all
      </button>
    </div>;
}

/** The primary filter row: campus, budget, move-in, type, then everything else. */
export function HousingPrimaryFilters({
  onOpenMore
}: {
  onOpenMore: () => void;
}) {
  const {
    intent,
    setIntent,
    category,
    setCategory,
    filters
  } = useHousing();
  const advanced = filters.facilities.length > 0 || filters.verifiedOnly || filters.maxMinutes < 60;
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-1926" data-mp="3j6vsjy0edufd">
      <FilterField label="Campus" hint="Change campus" icon={<MapPinIcon className="h-4 w-4" data-id="element-1928" data-mp="3sys3warhvh1j" />} data-id="element-1927" data-mp="3o19r29acwho9">
        <select value={intent.campusId} onChange={event => setIntent({
        ...intent,
        campusId: event.target.value
      })} className="w-full bg-transparent text-body font-medium text-ink focus:outline-none" aria-label="Campus" data-id="element-1929" data-mp="3sxqhja51384i">
          {campusOptions.map(option => <option key={option.id} value={option.id} data-id="element-1930" data-mp="3585wmlpbe0sl">
              {option.label}
            </option>)}
        </select>
      </FilterField>

      <FilterField label="Budget / year" hint="Rent ceiling" icon={<WalletIcon className="h-4 w-4" data-id="element-1932" data-mp="klcj86zo3lt3" />} data-id="element-1931" data-mp="kjrnwf5j3yu6">
        <select value={intent.budgetMax} onChange={event => setIntent({
        ...intent,
        budgetMax: Number(event.target.value)
      })} className="w-full bg-transparent text-body font-medium text-ink focus:outline-none" aria-label="Maximum rent per year" data-id="element-1933" data-mp="phjmfs0oij7c">
          {BUDGETS.map(amount => <option key={amount} value={amount} data-id="element-1934" data-mp="208ps6pc5cwgb">
              Up to ₦{amount.toLocaleString()}
            </option>)}
        </select>
      </FilterField>

      <FilterField label="Move-in date" hint="When you need it" icon={<CalendarIcon className="h-4 w-4" data-id="element-1936" data-mp="1mast9ma80dyh" />} data-id="element-1935" data-mp="1qcmp9h9h0tsb">
        <select value={intent.moveIn} onChange={event => setIntent({
        ...intent,
        moveIn: event.target.value
      })} className="w-full bg-transparent text-body font-medium text-ink focus:outline-none" aria-label="Move-in date" data-id="element-1937" data-mp="1m9q69pg0qvnu">
          {MOVE_INS.map(option => <option key={option.value} value={option.value} data-id="element-1938" data-mp="3bp7dpue7heo6">
              {option.label}
            </option>)}
        </select>
      </FilterField>

      <FilterField label="Accommodation" hint="Type of home" icon={<LayoutListIcon className="h-4 w-4" data-id="element-1940" data-mp="14zdu8pyxq9xe" />} data-id="element-1939" data-mp="150xawhoi6mzp">
        <select value={category} onChange={event => setCategory(event.target.value as typeof category)} className="w-full bg-transparent text-body font-medium text-ink focus:outline-none" aria-label="Accommodation type" data-id="element-1941" data-mp="19sfap2esihj3">
          <option value="All" data-id="element-1942" data-mp="1nrygvpo1jisr">All types</option>
          {KINDS.map(kind => <option key={kind} value={kind} data-id="element-1943" data-mp="1nq3vybcrrqoo">
              {kind}
            </option>)}
        </select>
      </FilterField>

      <button type="button" onClick={onOpenMore} className="relative flex min-h-[56px] items-center justify-center gap-2 rounded-xl border border-line bg-surface px-4 text-body font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1944" data-mp="1duac06wq0uwv">
        <SlidersHorizontalIcon aria-hidden className="h-5 w-5" data-id="element-1945" data-mp="7dgirciz34uy" />
        More filters
        {advanced && <span aria-label="Advanced filters are active" className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-brand-700" data-id="element-1946" data-mp="2lky2orx3l0lh" />}
      </button>
    </div>;
}
function FilterField({
  label,
  hint,
  icon,
  children
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div className="flex min-h-[56px] items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2" data-id="element-1947" data-mp="2qiptfim0ago3">
      <span aria-hidden className="shrink-0 text-brand-700" data-id="element-1948" data-mp="1xw55yt8niopt">
        {icon}
      </span>
      <span className="min-w-0 flex-1" data-id="element-1949" data-mp="2bn9xt95s2cpl">
        {children}
        <span className="block text-caption text-ink-subtle" data-id="element-1950" data-mp="2bouscys3dvng">{hint}</span>
      </span>
      <span className="sr-only" data-id="element-1951" data-mp="26vie9il1h78y">{label}</span>
    </div>;
}
export function HousingSort() {
  const {
    filters,
    setFilters
  } = useHousing();
  return <label className="flex items-center gap-2 text-body-sm text-ink-muted" data-id="element-1952" data-mp="1f5dot5if9u0v">
      Sort by
      <select value={filters.sort} onChange={event => setFilters({
      ...filters,
      sort: event.target.value as typeof filters.sort
    })} className="min-h-[44px] rounded-xl border border-line bg-surface px-3 text-body font-medium text-ink" data-id="element-1953" data-mp="1f3kr4fpsek50">
        <option value="recommended" data-id="element-1954" data-mp="2d2z3qsrmtmip">Recommended</option>
        <option value="price_asc" data-id="element-1955" data-mp="2i07kcox4tihu">Lowest rent</option>
        <option value="distance" data-id="element-1956" data-mp="2hxl5e47ww0pl">Closest to campus</option>
      </select>
    </label>;
}

/**
 * List / Split / Map. On small screens the split option is withheld — a map
 * squeezed beside cards at 390px serves neither, so map becomes a full view.
 */
export function HousingViewToggle() {
  const {
    view,
    setView
  } = useHousing();
  const options: {
    value: HousingView;
    label: string;
    icon: React.ReactNode;
    desktopOnly?: boolean;
  }[] = [{
    value: 'list',
    label: 'List',
    icon: <LayoutListIcon className="h-4 w-4" data-id="element-1957" data-mp="35rczf1v906r3" />
  }, {
    value: 'split',
    label: 'Split',
    icon: <SlidersHorizontalIcon className="h-4 w-4" data-id="element-1958" data-mp="3ama8u9wfwi4i" />,
    desktopOnly: true
  }, {
    value: 'map',
    label: 'Map',
    icon: <MapIcon className="h-4 w-4" data-id="element-1959" data-mp="3ftqf4idv2ecg" />
  }];
  return <div role="group" aria-label="How results are shown" className="inline-flex rounded-xl border border-line bg-surface p-1" data-id="element-1960" data-mp="3fs5lcuzdezfn">
      {options.map(option => <button key={option.value} type="button" onClick={() => setView(option.value)} aria-pressed={view === option.value} className={`flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-body-sm font-medium transition-colors duration-150 ease-out ${option.desktopOnly ? 'hidden lg:flex' : ''} ${view === option.value ? 'bg-brand-700 text-white hover:text-white' : 'text-ink hover:bg-canvas'}`} data-id="element-1961" data-mp="36u3n2n70ukw2">
          <span aria-hidden data-id="element-1962" data-mp="q5zhzmnk2x9c">{option.icon}</span>
          {option.label}
        </button>)}
    </div>;
}
export function MoreFiltersDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    filters,
    setFilters,
    results
  } = useHousing();
  return <Modal open={open} onClose={onClose} title="More filters" data-id="element-1963" data-mp="1c245yfiikrk3">
      <FilterRail filters={filters} onChange={setFilters} resultCount={results.length} data-id="element-1964" data-mp="1q0av979edj85" />
      <div className="mt-4 flex justify-end" data-id="element-1965" data-mp="1l0g4d3pmgtn3">
        <Button variant="primary" onClick={onClose} data-id="element-1966" data-mp="1kzmwu0iua03w">
          Show {results.length} {results.length === 1 ? 'home' : 'homes'}
        </Button>
      </div>
    </Modal>;
}
export function SearchAsIMove() {
  const {
    searchAsMoved,
    setSearchAsMoved
  } = useHousing();
  return <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border border-line bg-surface px-3 text-body-sm text-ink shadow-sm" data-id="element-1967" data-mp="28p8wep34ihcf">
      <input type="checkbox" checked={searchAsMoved} onChange={event => setSearchAsMoved(event.target.checked)} className="h-5 w-5 rounded border-line text-brand-700" data-id="element-1968" data-mp="2ioggdv4d5a8i" />
      Search as I move the map
    </label>;
}